<template>
  <Content>
    <div class="container py-4">
      <!-- وضعیت لودینگ -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-teal" role="status">
          <span class="visually-hidden">در حال بارگذاری...</span>
        </div>
      </div>

      <!-- پیام خطا یا خطای 404 (محصولی یافت نشد) -->
      <div
        v-else-if="errorMessage"
        class="alert text-center my-4 py-4 border-0 shadow-sm"
        :class="isNotFound ? 'alert-info bg-light text-muted' : 'alert-danger'"
        role="alert"
      >
        <i
          :class="
            isNotFound
              ? 'bi bi-box-seam fs-2 d-block mb-2 text-teal'
              : 'bi bi-exclamation-triangle fs-2 d-block mb-2 text-danger'
          "
        ></i>
        <span class="fw-semibold">{{ errorMessage }}</span>
      </div>

      <!-- محتوای اصلی -->
      <div v-else>
        <!-- وضعیت لیست خالی بدون خطا -->
        <div v-if="products.length === 0" class="text-center py-5 text-muted">
          <i class="bi bi-box-seam fs-2 d-block mb-2 text-teal"></i>
          <span>{{
            currentLang.noProducts ||
            (lang === "fa" ? "محصولی یافت نشد." : "No products found.")
          }}</span>
        </div>

        <!-- گرید محصولات -->
        <div v-else class="row g-4">
          <div
            v-for="product in products"
            :key="product.id"
            class="col-12 col-sm-6 col-md-4 col-lg-3"
          >
            <div class="card product-card h-100 border-0 shadow-sm">
              <div
                class="ratio ratio-4x3 bg-light overflow-hidden position-relative"
              >
                  <a
              class="product-image-wrapper"
              :href="`${currentLang.urls.Product}/${
                lang === 'fa' ? product.slug_fa : product.slug_en
              }`"
              draggable="false"
            >
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="lang === 'fa' ? product.name_fa : product.name_en"
                  class="w-100 h-100 object-fit-cover"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="w-100 h-100 d-flex align-items-center justify-content-center text-muted"
                >
                  <i class="bi bi-image fs-1 opacity-50"></i>
                </div>
                </a>
              </div>
            
              <div class="card-body d-flex flex-column">
                <span class="badge category-span align-self-start mb-2">
                  {{ product.category }}
                </span>

                <h6 class="fw-bold mb-2 product-title">
                  {{ lang === "fa" ? product.name_fa : product.name_en }}
                </h6>

                <div
                  class="line-price-stock d-flex justify-content-between align-items-center mb-3"
                >
                  <span class="fw-bold text-price">
                    {{ formatPrice(product.price) }}
                  </span>
                  <small
                    :class="product.stock === 0 ? 'text-danger' : 'text-muted'"
                  >
                    {{
                      product.stock > 0
                        ? `${currentLang.stock}: ${product.stock}`
                        : `${currentLang.outOfStock}`
                    }}
                  </small>
                </div>

                <button
                  v-if="!isInCart(product.id)"
                  type="button"
                  class="product-button"
                  :disabled="product.stock <= 0"
                  @click="handleAddToCart(product)"
                  :aria-label="
                    lang === 'fa'
                      ? `افزودن ${product.name_fa} به سبد خرید`
                      : `Add ${product.name_en} to cart`
                  "
                >
                  <span>
                    <i class="bi bi-cart fs-5 position-relative"></i>
                    {{currentLang.addToCart  }}
                  </span>
                </button>

                <button
                  v-else
                  type="button"
                  class="product-button remove"
                  @click="handleRemoveFromCart(product)"
                  :aria-label="
                    lang === 'fa'
                      ? `حذف ${product.name_fa} از سبد خرید`
                      : `Remove ${product.name_en} from cart`
                  "
                >
                  <span>
                    <i class="bi bi-trash fs-5 position-relative"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- صفحه‌بندی -->
        <nav
          v-if="pagination.total_pages > 1"
          class="mt-5 d-flex justify-content-center"
          aria-label="Product Pagination"
        >
          <ul class="pagination custom-pagination align-items-center mb-0">
            <li
              class="page-item"
              :class="{ disabled: pagination.current_page === 1 }"
            >
              <button
                class="page-link pagination-btn"
                @click="changePage(pagination.current_page - 1)"
                :disabled="pagination.current_page === 1"
                aria-label="قبلی"
              >
                <i class="bi bi-chevron-right"></i>
              </button>
            </li>

            <li
              v-for="page in pagination.total_pages"
              :key="page"
              class="page-item"
              :class="{ active: pagination.current_page === page }"
            >
              <button
                class="page-link page-number-btn"
                @click="changePage(page)"
              >
                {{
                  Number(page).toLocaleString(lang === "fa" ? "fa-IR" : "en-US")
                }}
              </button>
            </li>

            <li
              class="page-item"
              :class="{
                disabled: pagination.current_page === pagination.total_pages,
              }"
            >
              <button
                class="page-link pagination-btn"
                @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.total_pages"
                aria-label="بعدی"
              >
                <i class="bi bi-chevron-left"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <footerAdmin></footerAdmin>
  </Content>
</template>






<script setup>
import Content from "@/components/AdminContent.vue";
import footerAdmin from "@/components/AdminFooter.vue";
import { ref, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import api from "@/plugins/axios";
import { useCart } from "@/stores/cart";

const route = useRoute();
import { lang } from "@/plugins/lang";

import enText from "@/locales/en/productCategory";
import faText from "@/locales/fa/productCategory";

const translations = {
  en: enText,
  fa: faText,
};

const currentLang = computed(() => translations[lang.value]);

const products = ref([]);
const loading = ref(false);
const errorMessage = ref("");
const isNotFound = ref(false);

const pagination = ref({
  total: 0,
  count: 0,
  from: 1,
  to: 1,
  per_page: 15,
  current_page: 1,
  total_pages: 1,
});





const { addToCart, isInCart, removeFromCart } = useCart();

const handleAddToCart = (product) => {
  if (!product || isInCart(product.id)) return

  const result = addToCart(product.id, 1, product.stock,product.price)

  if (!result.success) {
    alert(result.message)
  }
}

const handleRemoveFromCart = (product) => {
  if (!product) return

  const result = removeFromCart(product.id)

  if (!result.success) {
    alert(result.message)
  }
}

/*
|--------------------------------------------------------------------------
| دریافت محصولات دسته‌بندی و صفحه‌بندی
|--------------------------------------------------------------------------
*/
const getProducts = async (page = 1) => {
  loading.value = true;
  errorMessage.value = "";
  isNotFound.value = false;

  try {
    const slug = route?.params?.slug || "story-book1";
    const response = await api.get(`/general/product-category/${slug}`, {
      params: {
        page: page,
      },
    });

    /*
     * طبق پاسخ سرور:
     * response.data.data.products
     * response.data.data.pagination
     */
    if (response.data?.success) {
      products.value = response.data.data?.products || [];

      if (response.data.data?.pagination) {
        pagination.value = response.data.data.pagination;
      }
    } else {
      products.value = [];
      errorMessage.value =
        response.data?.message ||
        currentLang.value?.noProducts ||
        (lang.value === "fa" ? "محصولی یافت نشد." : "No products found.");
      isNotFound.value = true;
    }
  } catch (error) {
    console.error("Error fetching category products:", error);

    products.value = [];

    // مدیریت خطای 404
    if (error.response?.status === 404) {
      isNotFound.value = true;
      errorMessage.value =
        currentLang.value?.noProducts ||
        (lang.value === "fa" ? "محصولی یافت نشد." : "No products found.");
    } else {
      isNotFound.value = false;
      errorMessage.value =
        error.response?.data?.message ||
        (lang.value === "fa"
          ? "خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید."
          : "Error connecting to server. Please try again.");
    }
  } finally {
    loading.value = false;
  }
};

/*
|--------------------------------------------------------------------------
| تغییر صفحه
|--------------------------------------------------------------------------
*/
const changePage = (page) => {
  if (
    page >= 1 &&
    page <= pagination.value.total_pages &&
    page !== pagination.value.current_page
  ) {
    pagination.value.current_page = page;
    getProducts(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

/*
|--------------------------------------------------------------------------
| توابع کمکی
|--------------------------------------------------------------------------
*/
const formatPrice = (price) => {
  if (price === null || price === undefined)
    return `۰ ${currentLang.value?.toman || "تومان"}`;

  return `${Number(price).toLocaleString("fa-IR")} ${
    currentLang.value?.toman || "تومان"
  }`;
};

const handleImageError = (e) => {
  e.target.style.display = "none";
};

/*
|--------------------------------------------------------------------------
| هوک‌ها و تماشاگرها
|--------------------------------------------------------------------------
*/
onMounted(() => {
  getProducts();
});

if (route) {
  watch(
    () => route.params?.slug,
    () => {
      pagination.value.current_page = 1;
      getProducts(1);
    }
  );
}
</script>

<style scoped>

.product-button.remove {
  background-color: #dc3545;
  color: #fff;
}

.product-button.remove:hover {
  background-color: #bb2d3b;
}


.product-button {

 background: #00cdc4;
  color: #fff;
  border: none;
  box-shadow: 0 6px 16px rgba(0, 205, 196, 0.25);
  transition: all 0.25s ease;
  flex: 0 0 38px;

 
}

.product-button span {
  font-size: 15px;
  line-height: 1;
}



.product-button:hover {

  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 205, 196, 0.35);
}


.text-teal {
  color: #00cdc4 !important;
}

.category-span {
  background: #6cceff;
  color: #18324b;
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.3;
}

.product-card {
  border-radius: 16px;
  overflow: hidden;
  max-width: 100%;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  background: linear-gradient(180deg, #ffffff 0%, #f8fcff 100%);
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.12) !important;
}

.product-title {
  min-height: 48px;
  line-height: 1.5;
  text-align: right;
  color: #18324b;
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.text-price {
  color: #18324b;
}

.btn-cart {
  background: #00cdc4;
  color: #fff;
  border: none;
  box-shadow: 0 6px 16px rgba(0, 205, 196, 0.25);
  transition: all 0.25s ease;
}

.btn-cart:hover {
  background: linear-gradient(135deg, #00cdc4 0%, #00a8a0 100%);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 205, 196, 0.35);
}

/* استایل‌های اختصاصی صفحه‌بندی */
.custom-pagination {
  gap: 6px;
}

.custom-pagination .page-item .page-link {
  border: none;
  background: #ffffff;
  color: #18324b;
  font-weight: bold;
  border-radius: 12px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  min-width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.custom-pagination .page-item.active .page-link {
  background: #00cdc4;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 205, 196, 0.35);
}

.custom-pagination .page-item:not(.active):not(.disabled) .page-link:hover {
  background: #f0fbfc;
  color: #00cdc4;
  transform: translateY(-2px);
}

.custom-pagination .page-item.disabled .page-link {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f8f9fa;
}

@media (max-width: 575.98px) {
  .custom-pagination .page-item .page-link {
    min-width: 34px;
    height: 34px;
    font-size: 0.85rem;
  }
}
</style>


























































 

